import type {ElectronicClocks_ScreenDelayModel} from "../../../../../api";
import type {DeviceListItem, DevicesResponse} from "../../../../modals/modals.model";
import {
  LedPanelControlValue,
  LedPanelDurationField,
  LedPanelDurations,
  LedPanelMeteo,
  LedPanelMeteoField,
  RgbColor
} from "./LedPanel.model";

export const defaultMeteoSourceLabel = "По координатам";

export const operatingModes = [
  {label: "Ведущий (Master)", value: "Master"},
  {label: "Ведомый (Slave)", value: "Slave"}
];

export const clockStyles = [
  {label: "Блочный", value: "Block"},
  {label: "Современный", value: "Modern"},
  {label: "Стандартный", value: "Standard"},
  {label: "Семисегментный", value: "SevenSegment"}
];

export const dotModes = [
  {label: "Постоянные", value: "Constant"},
  {label: "Мигающие", value: "Blinking"},
  {label: "Анимированные", value: "Animated"}
];

export const getDurationFields = (
  sensorTemperature?: number
): LedPanelDurationField[] => [
  ["time", "Время"],
  ["date", "Дата"],
  ["temperature", `Температура датчика`],
  ["storedTemperature", "Внешняя температура"],
  ["storedPressure", "Давление"],
  ["storedHumidity", "Влажность"]
];

export const meteoFields: LedPanelMeteoField[] = [
  ["temperature", "Температура"],
  ["pressure", "Давление"],
  ["humidity", "Влажность"]
];

export const emptyColor: RgbColor = {red: 0, green: 0, blue: 0};
export const emptyDurations: LedPanelDurations = {
  time: 0,
  date: 0,
  temperature: 0,
  storedTemperature: 0,
  storedPressure: 0,
  storedHumidity: 0
};
export const emptyMeteo: LedPanelMeteo = {temperature: 0, pressure: 0, humidity: 0};

export const clampByte = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

export const toHex = ({red, green, blue}: RgbColor) =>
  `#${[red, green, blue].map((value) => clampByte(value).toString(16).padStart(2, "0")).join("")}`;

export const fromHex = (value: string): RgbColor => ({
  red: parseInt(value.slice(1, 3), 16),
  green: parseInt(value.slice(3, 5), 16),
  blue: parseInt(value.slice(5, 7), 16)
});

export const toColor = (value?: Partial<RgbColor>): RgbColor => ({
  red: Number(value?.red ?? 0),
  green: Number(value?.green ?? 0),
  blue: Number(value?.blue ?? 0)
});

export const toDurations = (
  value?: Partial<Record<keyof LedPanelDurations, number>>
): LedPanelDurations => ({
  time: Number(value?.time ?? 0),
  date: Number(value?.date ?? 0),
  temperature: Number(value?.temperature ?? 0),
  storedTemperature: Number(value?.storedTemperature ?? 0),
  storedPressure: Number(value?.storedPressure ?? 0),
  storedHumidity: Number(value?.storedHumidity ?? 0)
});

export const toMeteo = (value?: Partial<LedPanelMeteo>): LedPanelMeteo => ({
  temperature: Number(value?.temperature ?? 0),
  pressure: Number(value?.pressure ?? 0),
  humidity: Number(value?.humidity ?? 0)
});

export const normalizeDuration = (value: LedPanelControlValue) =>
  Math.max(0, Math.min(99, Number(value ?? 0)));

export const normalizeMeteoValue = (value: LedPanelControlValue) =>
  Math.round(Number(value ?? 0));

export const toScreenDelay = (durations: LedPanelDurations): ElectronicClocks_ScreenDelayModel => ({
  time: durations.time,
  date: durations.date,
  temperatureFromSensor: durations.temperature,
  temperature: durations.storedTemperature,
  pressure: durations.storedPressure,
  humidity: durations.storedHumidity
});

export const formatMeteoSourceLabel = (device: DeviceListItem) => {
  if (device.kind === "coords") return defaultMeteoSourceLabel;

  const title = device.kind === "clock" ? "Часовой механизм" : "Блок датчиков";
  const model = device.model ? ` ${device.model}` : "";
  const address = device.address ? ` | ${device.address}` : "";
  return `${title}${model}${address}`;
};

export const formatUnknownMeteoSourceLabel = (selectedId: string) =>
  `Устройство ${selectedId}`;

export const getMeteoSourceLabel = (
  response: DevicesResponse,
  selectedId: string | null
) => {
  if (!selectedId) return defaultMeteoSourceLabel;

  const devices: DeviceListItem[] = [
    ...(response.clocks ?? []).map((item) => ({...item, kind: "clock" as const})),
    ...(response.sensors ?? []).map((item) => ({...item, kind: "sensor" as const}))
  ];
  const selected = devices.find((item) => String(item.id) === selectedId);
  return selected ? formatMeteoSourceLabel(selected) : formatUnknownMeteoSourceLabel(selectedId);
};

export const getMeteoSourceId = (device: DeviceListItem) =>
  device.kind === "coords" || device.id === "coordinates" ? null : String(device.id);

export const formatWorkdays = (workdays: number | null) =>
  workdays == null ? "—" : `${workdays} (0x${workdays.toString(16).toUpperCase()})`;
